import { TableBody, TableCell, TableHead, TableRow, TableProps, Typography, TableContainer, Rating, Button, Box, Dialog, Stack, TextField, Divider } from "@mui/material"
import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect, useRef } from "react";
import { CompanyAdvisorLink } from "../CompanyAdvisorLink";
import { AdvisorTable } from "../../styles/AdvisorStyles";
import { DoNotDisturb, Star, StarBorder } from '@mui/icons-material';
import { DateDisplay } from "../../../meeting";
import { AdvisorPreview } from "../../../advisor";
import { useParams } from "react-router-dom";
import Loading from "../../../../components/Loading";
import { IMeeting, IProject, SchedulerType } from "../../../../types";
import { AdvisorCompany, AdvisorProfileSummary } from "../../../advisor";
import { RatingDialog } from "./RatingDialog";
import { RATING_LABELS } from "../../../../constants/ratings";
import { ScoreCircle } from "../../styles/AdvisorStyles";
import { DisplayBoxBase } from "../../../../components/shared/StyledComponents";
import { Lightbulb, Calendar, CalendarOff, LightbulbOff, Info, Ban } from "lucide-react";
import ClientProvider from "../../../../modules/ClientProvider";
import { Link } from "react-router-dom";

const meetingDescriptions: Partial<Record<SchedulerType, { description: string, tips: string[], avoid: string[] }>> = {
    [SchedulerType.INTRO]: {
        description: "A short, initial meeting between the Founder (or company sponsor) and the advisor that engages them and set the tone for the relationship.",
        tips: [
            "Introduce yourself and the company",
            "Welcome the executive as a light advisor",
            "Ask about their background & build rapport"
        ],
        avoid: [
            "Doing a demo - that comes later",
            "Probing about their current company",
            "\"Selling\""
        ]
    },
    [SchedulerType.DEMO]: {
        description: "A short product demo or discussion where the executive can share what they consider most valuable, while getting them to think about and name how your product could help them now.",
        tips: [
            "Be curious about what they find most valuable",
            "Be curious about what they think is missing",
            "Engage them in a conversation"
        ],
        avoid: [
            "Selling too hard",
            "Probing about their current company",
            "Treating them as a company vs an individual"
        ]
    },
    [SchedulerType.TRIAL]: {
        description: "This is a sales call. The goal is to discuss an implementation with an advisor that has raised their hand and indicated they’d like to talk about using the product at their company.",
        tips: [
            "Acknowledge their history as an advisor",
            "Understand their use case for the product",
            "Provide commercial terms and pricing"
        ],
        avoid: [
            "Treating the advisor as an arm's length prospect"
        ]
    }
}

export const SingleList = ({ project, meetings, reloadData, showType = false, type }: { project: IProject, meetings: IMeeting[], reloadData: () => void, showType?: boolean, type: SchedulerType }) => {

    const { getAccessTokenSilently } = useAuth0();
    const [hoveredMeeting, setHoveredMeeting] = useState<string | null>(null);
    const [isRatingDialogOpen, setIsRatingDialogOpen] = useState(false);
    const [suggestedRating, setSuggestedRating] = useState<number | null>(null);
    const initialRating = new URLSearchParams(window.location.search).get("rt");
    const [scheduler, setScheduler] = useState<any>(null);
    const [schedulerFetched, setSchedulerFetched] = useState(false);

    const schedulerClient = ClientProvider.provideSchedulerClient();
    const projectClient = ClientProvider.provideProjectClient();

    const { meetingUuid } = useParams();

    const fetchScheduler = async () => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await schedulerClient.getSchedulerByType(project.tag, type, accessToken);
        if (error) {
            console.error("Error fetching scheduler:", error);
        } else if (data) {
            setScheduler(data.scheduler);
        }
        setSchedulerFetched(true);
    }

    useEffect(() => {
        fetchScheduler();
    }, [project.tag, type]);

    useEffect(() => {
        if (meetingUuid) {
            setHoveredMeeting(meetingUuid);
            setIsRatingDialogOpen(true);
            window.history.replaceState({}, '', window.location.pathname.replace(`/${meetingUuid}`, `/`));
        }
        if (initialRating !== null) {
            setSuggestedRating(parseInt(initialRating));
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.delete('rt');
            window.history.replaceState({}, '', window.location.pathname + (searchParams.toString() ? '?' + searchParams.toString() : ''));
        }
    }, [meetingUuid, initialRating]);

    const handleRateAndOpportunity = async (meetingUuid: string, rating: number | null, markAsOpportunity: boolean, comment: string | null) => {
        if (rating === null) return;

        const accessToken = await getAccessTokenSilently();

        // Rate the meeting
        const ratingResult = await projectClient.rateMeeting(project.id, meetingUuid, rating, comment, accessToken);

        // Update advisor stage based on opportunity status
        if (ratingResult.data) {
            const meeting = meetings.find((m: IMeeting) => m.uuid === meetingUuid);
            const stage = markAsOpportunity ? "opportunity" : "advisor";
            if (meeting?.advisor.uuid) {
                await projectClient.updateAdvisorStage(project.id, meeting.advisor.uuid, stage, accessToken);
            }
        }

        if (!isRatingDialogOpen) {
            reloadData();
        }
    };

    const handleMarkAsOpportunity = async (advisorUuid: string, event: React.MouseEvent) => {
        event.stopPropagation();

        // If the meeting hasn't been rated, open the rating dialog
        if (meetings.find((meeting: IMeeting) => meeting.advisor.uuid === advisorUuid)?.ratings?.length === 0) {
            setIsRatingDialogOpen(true);
            return;
        }

        const accessToken = await getAccessTokenSilently();
        const { data, error } = await projectClient.updateAdvisorStage(project.id, advisorUuid, "opportunity", accessToken);
        if (data) {
            reloadData();
        }
    }

    const handleMarkAsCustomer = async (advisorUuid: string, event: React.MouseEvent) => {
        event.stopPropagation();

        const accessToken = await getAccessTokenSilently();
        const { data, error } = await projectClient.updateAdvisorStage(project.id, advisorUuid, "customer", accessToken);
        if (data) {
            reloadData();
        }
    }

    const handleCloseRatingDialog = () => {
        setIsRatingDialogOpen(false);
        reloadData();
    }

    if (!meetings) {
        return <Loading />;
    }

    const selectedMeeting = meetings.find((m: IMeeting) => m.uuid === hoveredMeeting);
    const upcomingMeetings = meetings.filter((m: IMeeting) => m.event_start && new Date(m.event_start) > new Date());
    const completedMeetings = meetings.filter((m: IMeeting) => m.event_start && new Date(m.event_start) < new Date());

    if (!schedulerFetched) {
        return <Loading />;
    }

    return (
        <>
            {isRatingDialogOpen && hoveredMeeting && selectedMeeting && (
                <RatingDialog
                    meeting={selectedMeeting}
                    setIsRatingDialogOpen={setIsRatingDialogOpen}
                    onRate={handleRateAndOpportunity}
                    onClose={handleCloseRatingDialog}
                    suggestedRating={suggestedRating}
                />
            )}

            {project.effective_scheduler_properties?.[type]?.description && (
                <DisplayBoxBase className="gray column" sx={{ marginBottom: "40px", gap: "20px" }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start" divider={<Divider orientation="vertical" flexItem />}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ display: "flex", alignItems: "center", gap: "5px", fontWeight: "bold", marginBottom: "10px" }}>
                                <Info size={16} /> Meeting description
                            </Typography>
                            <Typography variant="body1">
                                {meetingDescriptions[type as SchedulerType]?.description}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ display: "flex", alignItems: "center", gap: "5px", fontWeight: "bold", marginBottom: "10px" }}>
                                <Lightbulb size={16} /> Tips for a good meeting
                            </Typography>
                            <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
                                {meetingDescriptions[type as SchedulerType]?.tips.map((tip) => (
                                    <li key={tip}>{tip}</li>
                                ))}
                            </ul>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ display: "flex", alignItems: "center", gap: "5px", fontWeight: "bold", marginBottom: "10px" }}>
                                <Ban size={16} /> Try to avoid
                            </Typography>
                            <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
                                {meetingDescriptions[type as SchedulerType]?.avoid.map((avoid) => (
                                    <li key={avoid}>{avoid}</li>
                                ))}
                            </ul>
                        </Box>
                    </Stack>
                </DisplayBoxBase>
            )}

            {(!scheduler || !scheduler.scheduling_url || scheduler.scheduling_url === "") && (
                <Box sx={{ marginBottom: "40px" }}>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CalendarOff size={48} />
                        <Typography variant="h6" gutterBottom sx={{ marginTop: "20px" }}>
                            Your <strong>{project.effective_scheduler_properties?.[type]?.name}</strong> calendar is not setup.
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            Please configure your calendar <Link to={`/c/calendar`}>here</Link>.
                        </Typography>

                    </Box>
                </Box>
            )}

            {((scheduler?.scheduling_url && scheduler?.scheduling_url !== "") || upcomingMeetings.length > 0 || completedMeetings.length > 0) && (
                <>


                    {/* Upcoming Meetings Table */}
                    <h5>Upcoming Meetings</h5>
                    <TableContainer sx={{ marginTop: "20px", marginBottom: "40px" }}>
                        <AdvisorTable sx={{
                            borderTop: "1px solid #000000",
                            tableLayout: 'fixed',
                            width: '100%'
                        }}>
                            <TableHead>

                                <TableRow key="header" className="header">
                                    <TableCell className="center" sx={{ width: '120px' }}>Date</TableCell>
                                    <TableCell className="profile" sx={{ width: '200px' }}>Advisor</TableCell>
                                    <TableCell className="company" sx={{ width: '150px' }}>Company</TableCell>
                                    <TableCell className="center" sx={{ width: '80px' }}>Score</TableCell>
                                    <TableCell colSpan={2} className="left" sx={{ width: '240px' }}>Info</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {upcomingMeetings.sort((a: IMeeting, b: IMeeting) => new Date(a.event_start!).getTime() - new Date(b.event_start!).getTime()).map((meeting: IMeeting) => (
                                    <CompanyAdvisorLink uuid={meeting.advisor.uuid} key={`link-${meeting.uuid}`}>
                                        <TableRow key={meeting.uuid}>
                                            <TableCell className="center">
                                                <Stack spacing={0.5} alignItems="center">
                                                    <DateDisplay meeting={meeting} showTime={false} />
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(meeting.event_start!).toLocaleString('en-US', {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                timeZoneName: 'short'
                                                            })}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </TableCell>
                                            <TableCell className="profile"><AdvisorProfileSummary advisor={meeting.advisor} /></TableCell>
                                            <TableCell className="company"><AdvisorCompany advisor={meeting.advisor} /></TableCell>
                                            <TableCell className="center">
                                                <ScoreCircle
                                                    score={meeting.advisor.score || 0}
                                                    sx={{
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                        transition: 'transform 0.2s ease',
                                                        '&:hover': {
                                                            transform: 'scale(1.05)'
                                                        }
                                                    }}
                                                >
                                                    {meeting.advisor.score || '-'}
                                                </ScoreCircle>
                                            </TableCell>
                                            <TableCell colSpan={2} className="left" onClick={(e) => e.stopPropagation()}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: "0.875rem",
                                                        lineHeight: 1.4,
                                                        color: 'text.secondary'
                                                    }}
                                                >
                                                    {(() => {
                                                        try {
                                                            if (!meeting.advisor.highlights_json) return 'No highlights available';
                                                            const highlights = JSON.parse(meeting.advisor.highlights_json);
                                                            return highlights?.highlights?.[0] || 'No highlights available';
                                                        } catch {
                                                            return 'No highlights available';
                                                        }
                                                    })()}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </CompanyAdvisorLink>
                                ))}
                                {upcomingMeetings.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} className="center">
                                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                                <CalendarOff size={48} color="text.secondary" />
                                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                                    No upcoming meetings
                                                </Typography>

                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </AdvisorTable>
                    </TableContainer>

                    {/* Completed Meetings Table */}
                    <h5>Completed Meetings</h5>
                    <TableContainer sx={{ marginTop: "20px", marginBottom: "40px" }}>
                        <AdvisorTable sx={{
                            borderTop: "1px solid #000000",
                            tableLayout: 'fixed',
                            width: '100%'
                        }}>
                            <TableHead>
                                <TableRow key="subheader" className="header">
                                    <TableCell className="center" sx={{ width: '120px' }}>Date</TableCell>
                                    <TableCell className="profile" sx={{ width: '200px' }}>Advisor</TableCell>
                                    <TableCell className="company" sx={{ width: '150px' }}>Company</TableCell>
                                    <TableCell className="center" sx={{ width: '80px' }}>Score</TableCell>
                                    <TableCell className="center" sx={{ width: '120px' }}>Rating</TableCell>
                                    <TableCell className="center" sx={{ width: '120px' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {completedMeetings.sort((a: IMeeting, b: IMeeting) => new Date(b.event_start!).getTime() - new Date(a.event_start!).getTime()).map((meeting: IMeeting) => (
                                    <CompanyAdvisorLink uuid={meeting.advisor.uuid} key={`link-${meeting.uuid}`}>
                                        <TableRow key={meeting.uuid}>
                                            <TableCell className="center">
                                                <DateDisplay meeting={meeting} showTime={false} />
                                            </TableCell>
                                            <TableCell className="profile"><AdvisorProfileSummary advisor={meeting.advisor} /></TableCell>
                                            <TableCell className="company"><AdvisorCompany advisor={meeting.advisor} /></TableCell>
                                            <TableCell className="center">
                                                <ScoreCircle
                                                    score={meeting.advisor.score || 0}
                                                    sx={{
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                        transition: 'transform 0.2s ease',
                                                        '&:hover': {
                                                            transform: 'scale(1.05)'
                                                        }
                                                    }}
                                                >
                                                    {meeting.advisor.score || '-'}
                                                </ScoreCircle>
                                            </TableCell>

                                            <TableCell className="center" onClick={(e) => e.stopPropagation()} sx={{ width: '200px' }}>
                                                <Box sx={{
                                                    minHeight: '60px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    flexDirection: 'column',
                                                    gap: '5px'
                                                }}
                                                    onClick={() => {
                                                        setHoveredMeeting(meeting.uuid);
                                                        setIsRatingDialogOpen(true);
                                                    }}>
                                                    {meeting.ratings?.length && meeting.ratings[0].rating === 0 ? (
                                                        <Typography component="legend" variant="body2" sx={{ fontSize: "0.75rem" }}>
                                                            <DoNotDisturb sx={{ width: "16px", height: "16px", marginRight: "3px" }} /> Did not attend
                                                        </Typography>
                                                    ) : (
                                                        <>
                                                            <Rating
                                                                value={meeting.ratings?.length && meeting.ratings[0].rating ? meeting.ratings[0].rating : null}
                                                                max={4}
                                                                readOnly
                                                            />
                                                            <Typography component="legend" variant="body2" sx={{ fontSize: "0.75rem" }}>

                                                                {meeting.ratings?.length && meeting.ratings[0].rating ? (
                                                                    RATING_LABELS[meeting.ratings[0].rating]
                                                                ) : "Please rate the meeting"}
                                                            </Typography>
                                                        </>
                                                    )}

                                                </Box>
                                            </TableCell>
                                            <TableCell className="center" onClick={(e) => e.stopPropagation()}>
                                                {meeting.advisor.stage === "opportunity" ? (
                                                    <span className="link" onClick={(e) => {
                                                        setHoveredMeeting(meeting.uuid);
                                                        handleMarkAsCustomer(meeting.advisor.uuid, e);
                                                    }}>
                                                        Mark as Customer
                                                    </span>
                                                ) : meeting.advisor.stage === "advisor" ? (
                                                    <span className="link" onClick={(e) => {
                                                        setHoveredMeeting(meeting.uuid);
                                                        handleMarkAsOpportunity(meeting.advisor.uuid, e);
                                                    }}>
                                                        Mark as Opportunity
                                                    </span>
                                                ) : meeting.advisor.stage === "customer" ? (
                                                    <span>
                                                        Converted to Customer
                                                    </span>
                                                ) : (
                                                    <span>
                                                        ---
                                                    </span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </CompanyAdvisorLink>
                                ))}
                            </TableBody>
                        </AdvisorTable>
                    </TableContainer>
                </>
            )}

        </>
    )
}